import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { 
  Shield, Star, Search, Check, X, Loader2, ChevronDown, 
  Filter, Crown, AlertCircle, CheckCircle, Info, HelpCircle, RefreshCw, Eye
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import useInterstitialAd from "../hooks/useInterstitialAd";
import { hapticSuccess, hapticLight } from "../utils/haptics";
import { getEffectiveTier } from "../utils/features";
import { getDisplayName, getDisplayShortName } from "../utils/teamNameMapping";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Team colors for player avatars
const TEAM_COLORS = {
  "India": { bg: "from-orange-500 to-blue-600" },
  "Pakistan": { bg: "from-green-600 to-green-800" },
  "Australia": { bg: "from-yellow-400 to-green-600" },
  "England": { bg: "from-blue-700 to-red-600" },
  "South Africa": { bg: "from-green-500 to-yellow-500" },
  "New Zealand": { bg: "from-black to-gray-700" },
  "West Indies": { bg: "from-red-600 to-yellow-500" },
  "Sri Lanka": { bg: "from-blue-800 to-yellow-500" },
  "Afghanistan": { bg: "from-red-600 to-green-600" },
  "default": { bg: "from-gray-600 to-gray-800" }
};

const getTeamColor = (team) => TEAM_COLORS[team] || TEAM_COLORS["default"];

// Role requirements
const ROLE_REQUIREMENTS = {
  "Wicketkeeper": { min: 1, max: 4, label: "WK", icon: "🧤" },
  "Batsman": { min: 3, max: 6, label: "BAT", icon: "🏏" },
  "All-Rounder": { min: 1, max: 4, label: "AR", icon: "⭐" },
  "Bowler": { min: 3, max: 6, label: "BOWL", icon: "🎯" }
};

const ROLE_COLORS = {
  "Batsman": "bg-blue-500/20 text-blue-400 border-blue-500/30",
  "Bowler": "bg-red-500/20 text-red-400 border-red-500/30",
  "All-Rounder": "bg-purple-500/20 text-purple-400 border-purple-500/30",
  "Wicketkeeper": "bg-amber-500/20 text-amber-400 border-amber-500/30"
};

export default function TeamBuilderPage() {
  const { user, dbUser, getAuthHeader } = useAuth();
  const { showInterstitial } = useInterstitialAd();
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [roleReqs, setRoleReqs] = useState(ROLE_REQUIREMENTS);
  const [maxPerCountry, setMaxPerCountry] = useState(4);
  
  // Team state
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [captainId, setCaptainId] = useState(null);
  const [viceCaptainId, setViceCaptainId] = useState(null);
  const [teamName, setTeamName] = useState("");
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  
  // Existing team & transfer window
  const [existingTeam, setExistingTeam] = useState(null);
  const [transferWindow, setTransferWindow] = useState({ is_open: false, window: null });
  
  // Budget from Week 2 system
  const [userBudget, setUserBudget] = useState(100);
  const [budgetBreakdown, setBudgetBreakdown] = useState(null);
  
  // Show rules modal
  const [showRules, setShowRules] = useState(false);
  
  // Player detail overlay
  const [selectedPlayerDetail, setSelectedPlayerDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Draft Team state
  const [draftTeam, setDraftTeam] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [applyingDraft, setApplyingDraft] = useState(false);
  const [draftMode, setDraftMode] = useState(false); // Toggle to show draft vs actual team

  // Captain change state (Pro Max only, outside transfer window)
  const [showCaptainChangeModal, setShowCaptainChangeModal] = useState(false);
  const [changingCaptain, setChangingCaptain] = useState(false);
  const [newCaptainId, setNewCaptainId] = useState(null);
  const [showVcChangeModal, setShowVcChangeModal] = useState(false);
  const [changingVc, setChangingVc] = useState(false);
  const [newVcId, setNewVcId] = useState(null);

  // Player stats gating state (React state only, resets on refresh)
  const [statsLookupCount, setStatsLookupCount] = useState(0);

  // Weekly points breakdown toggle
  const [pointsView, setPointsView] = useState("season");
  const [weeklyBreakdown, setWeeklyBreakdown] = useState(null);
  const [statsGatedPlayerId, setStatsGatedPlayerId] = useState(null);
  const [isNativePlatform, setIsNativePlatform] = useState(false);

  // Refs for pull-to-refresh guard and team name prefill
  const hasUnsavedChangesRef = useRef(false);
  const hasPrefilledName = useRef(false);

  // Check if on native platform
  useEffect(() => {
    const checkPlatform = async () => {
      try {
        const { Capacitor } = await import("@capacitor/core");
        setIsNativePlatform(Capacitor.isNativePlatform());
      } catch {
        setIsNativePlatform(false);
      }
    };
    checkPlatform();
  }, []);

  // Get stats lookup limit based on tier
  const getStatsLookupLimit = () => {
    const tier = getEffectiveTier(dbUser);
    const limits = { free: 1, pro: 3, pro_plus: 5, pro_max: Infinity };
    return limits[tier] || 1;
  };

  // Get unique countries
  const countries = [...new Set(players.map(p => p.country))].sort();

  // Fetch data function
  const fetchData = useCallback(async (signal) => {
    try {
      setLoading(true);
      // Get fresh token for authenticated requests
      const token = user ? await user.getIdToken(true) : null;
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      
      // First fetch user's team to get existing player IDs
      let existingPlayerIds = [];
      if (user) {
        try {
          const teamsRes = await axios.get(`${API}/teams/my-teams`, { headers: authHeaders, signal });
          if (teamsRes.data.teams && teamsRes.data.teams.length > 0) {
            existingPlayerIds = teamsRes.data.teams[0].players || [];
          }
        } catch (e) {
          // Ignore errors fetching teams
        }
      }
      
      // Build players URL with active_only and include existing team players
      let playersUrl = `${API}/players?active_only=true`;
      if (existingPlayerIds.length > 0) {
        playersUrl += `&include_ids=${existingPlayerIds.join(",")}`;
      }
      
      const [playersRes, teamsRes, windowRes] = await Promise.all([
        axios.get(playersUrl, { signal }),
        user ? axios.get(`${API}/teams/my-teams`, { headers: authHeaders, signal }).catch(() => ({ data: { teams: [] } })) : Promise.resolve({ data: { teams: [] } }),
        axios.get(`${API}/transfer-window/status`, { signal })
      ]);
      
      // Check if component unmounted
      if (signal?.aborted) return;
      
      setPlayers(playersRes.data.players);
      setTransferWindow(windowRes.data);
      
      // Don't overwrite unsaved changes — still update players and transfer window
      if (hasUnsavedChangesRef.current) {
        return;
      }
      
      if (teamsRes.data.teams.length > 0) {
        const team = teamsRes.data.teams[0];
        setExistingTeam(team);
        setTeamName(team.name);
        setCaptainId(team.captain_id);
        setViceCaptainId(team.vice_captain_id);
        const teamPlayers = playersRes.data.players.filter(p => team.players.includes(p.id));
        setSelectedPlayers(teamPlayers);
      }
      
      // Fetch user's actual budget for Week 2
      if (user && !signal?.aborted) {
        try {
          const budgetRes = await axios.get(`${API}/budget/my-budget`, { headers: authHeaders, signal });
          if (budgetRes.data && budgetRes.data.budget) {
            setUserBudget(budgetRes.data.budget);
            setBudgetBreakdown(budgetRes.data.breakdown || null);
          }
        } catch (e) {
          if (e.name !== 'CanceledError') {
            console.error("Failed to fetch budget:", e);
          }
          // Keep default budget of 100 if budget fetch fails
        }
        
        // Fetch draft team
        try {
          const draftRes = await axios.get(`${API}/draft-teams/my-draft`, { headers: authHeaders, signal });
          if (draftRes.data && draftRes.data.draft_team) {
            setDraftTeam(draftRes.data);
          }
        } catch (e) {
          if (e.name !== 'CanceledError') {
            console.error("Failed to fetch draft team:", e);
          }
        }
      }
    } catch (e) {
      if (e.name !== 'CanceledError') {
        console.error("Failed to fetch data:", e);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    // Abort controller to cancel requests on unmount
    const abortController = new AbortController();
    fetchData(abortController.signal);
    
    // Cleanup function to abort requests on unmount
    return () => {
      abortController.abort();
    };
  }, [fetchData]);

  // Fetch tournament-aware composition rules (Women's T20 WC overrides IPL defaults)
  useEffect(() => {
    let active = true;
    axios.get(`${API}/composition-rules`).then(res => {
      if (!active) return;
      const r = res.data?.rules;
      if (!r) return;
      setMaxPerCountry(r.max_per_country || 4);
      setRoleReqs(prev => {
        const next = {};
        for (const role of Object.keys(prev)) {
          next[role] = {
            ...prev[role],
            min: r[role]?.min ?? prev[role].min,
            max: r[role]?.max ?? prev[role].max,
          };
        }
        return next;
      });
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  // Pre-fill team name from preferred_team_name (only once, only if no existing team)
  useEffect(() => {
    if (hasPrefilledName.current) return;
    if (!dbUser?.preferred_team_name) return;
    if (existingTeam) return;
    setTeamName(dbUser.preferred_team_name);
    hasPrefilledName.current = true;
  }, [dbUser?.preferred_team_name, existingTeam]);

  // Filter players
  const filteredPlayers = players.filter(player => {
    const matchesSearch = searchQuery === "" || 
      player.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      player.country.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === "" || player.role === roleFilter;
    const matchesCountry = countryFilter === "" || player.country === countryFilter;
    return matchesSearch && matchesRole && matchesCountry;
  });

  // Calculate role counts
  const getRoleCounts = () => {
    const counts = { "Wicketkeeper": 0, "Batsman": 0, "All-Rounder": 0, "Bowler": 0 };
    selectedPlayers.forEach(p => { counts[p.role] = (counts[p.role] || 0) + 1; });
    return counts;
  };

  // Calculate country counts
  const getCountryCounts = () => {
    const counts = {};
    selectedPlayers.forEach(p => { counts[p.country] = (counts[p.country] || 0) + 1; });
    return counts;
  };

  const roleCounts = getRoleCounts();
  const countryCounts = getCountryCounts();
  const totalCredits = selectedPlayers.reduce((sum, p) => sum + (p.credits || p.base_credits || 8), 0);
  const originalCredits = selectedPlayers.reduce((sum, p) => sum + (p.base_credits || 8), 0);
  const creditBudget = userBudget; // Use the fetched Week 2 budget
  
  // Determine view-only state for ACTUAL team saving
  const isNewUsersOnlyWindow = transferWindow.is_open && transferWindow.window?.new_users_only;
  const isViewOnlyForSave = (!transferWindow.is_open && existingTeam) || (isNewUsersOnlyWindow && existingTeam);
  
  // First-time users can ALWAYS create a team, even if transfer window is closed
  const isFirstTimeUser = !existingTeam;
  const canSaveTeam = isFirstTimeUser || transferWindow.is_open;
  
  // Allow editing for draft purposes even when actual team is view-only
  const isViewOnly = isViewOnlyForSave && !draftMode;
  
  // For locked/view-only teams, show original cost at creation time
  const displayCredits = isViewOnly ? originalCredits : totalCredits;

  // Check if player can be added with reason
  const isFinalsMode = transferWindow?.window?.finals_mode || false;
  const maxPerTeam = isFinalsMode ? 11 : 4;

  const getPlayerStatus = (player) => {
    if (selectedPlayers.find(p => p.id === player.id)) {
      return { canAdd: false, reason: "Already selected", selected: true };
    }
    if (selectedPlayers.length >= 11) {
      return { canAdd: false, reason: "Team full (11/11)", selected: false };
    }
    if (isFinalsMode) {
      // Finals mode: only 2 countries allowed, no per-team cap
      const countries = new Set(selectedPlayers.map(p => p.country));
      if (countries.size >= 2 && !countries.has(player.country)) {
        return { canAdd: false, reason: "Finals: only 2 teams allowed", selected: false };
      }
    } else {
      if ((countryCounts[player.country] || 0) >= maxPerCountry) {
        return { canAdd: false, reason: `Max ${maxPerCountry} from this team`, selected: false };
      }
    }
    const req = roleReqs[player.role];
    if (req && roleCounts[player.role] >= req.max) {
      return { canAdd: false, reason: `Max ${req.max} ${player.role}s reached`, selected: false };
    }
    if (totalCredits + (player.credits || player.base_credits || 8) > creditBudget) {
      return { canAdd: false, reason: "Over budget", selected: false };
    }
    return { canAdd: true, reason: null, selected: false };
  };

  // Toggle player selection
  const togglePlayer = (player) => {
    const status = getPlayerStatus(player);
    if (status.selected) {
      setSelectedPlayers(selectedPlayers.filter(p => p.id !== player.id));
      if (captainId === player.id) setCaptainId(null);
      if (viceCaptainId === player.id) setViceCaptainId(null);
    } else if (status.canAdd) {
      setSelectedPlayers([...selectedPlayers, player]);
    }
    hasUnsavedChangesRef.current = true;
  };

  // Validate team - returns array of issues
  const getValidationIssues = () => {
    const issues = [];
    
    // Players count
    if (selectedPlayers.length < 11) {
      issues.push({ type: "error", message: `Select ${11 - selectedPlayers.length} more player(s)`, field: "players" });
    }
    
    // Role requirements
    for (const [role, req] of Object.entries(roleReqs)) {
      if (roleCounts[role] < req.min) {
        issues.push({ type: "error", message: `Need ${req.min - roleCounts[role]} more ${role}(s)`, field: role });
      }
    }
    
    // Budget - only check when editing/creating, not for locked teams
    if (!isViewOnly && totalCredits > creditBudget) {
      issues.push({ type: "error", message: `Over budget by ${(totalCredits - creditBudget).toFixed(1)} Cr`, field: "budget" });
    }
    
    // Captain
    if (!captainId && selectedPlayers.length > 0) {
      issues.push({ type: "warning", message: "Select a Captain (C) - earns 2x points", field: "captain" });
    }
    
    // Vice Captain
    if (!viceCaptainId && selectedPlayers.length > 0) {
      issues.push({ type: "warning", message: "Select a Vice-Captain (VC) - earns 1.5x points", field: "viceCaptain" });
    }
    
    // Team name
    if (!teamName.trim()) {
      issues.push({ type: "warning", message: "Enter a team name", field: "teamName" });
    }
    
    return issues;
  };

  const validationIssues = getValidationIssues();
  const hasErrors = validationIssues.some(i => i.type === "error");
  const canSave = !hasErrors && captainId && viceCaptainId && teamName.trim();

  // Fetch weekly player points breakdown
  const fetchWeeklyBreakdown = async () => {
    if (weeklyBreakdown) return;
    try {
      const token = await user.getIdToken();
      const res = await axios.get(
        `${API}/teams/my-team/weekly-breakdown`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWeeklyBreakdown(res.data);
    } catch (e) {
      console.error("Failed to fetch weekly breakdown", e);
    }
  };

  // Fetch player detail for overlay with stats gating
  const openPlayerDetail = async (playerId) => {
    // Check lookup limit - applies to BOTH web and native
    const limit = getStatsLookupLimit();
    if (statsLookupCount >= limit) {
      // Show gated state - user needs to unlock
      setStatsGatedPlayerId(playerId);
      return;
    }

    // Increment count and fetch
    setStatsLookupCount(prev => prev + 1);
    setLoadingDetail(true);
    try {
      const res = await axios.get(`${API}/players/${playerId}/detail`);
      setSelectedPlayerDetail(res.data);
    } catch (e) {
      console.error("Failed to load player detail:", e);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Unlock stats via rewarded video
  const unlockStatsWithVideo = async () => {
    try {
      const { Capacitor } = await import("@capacitor/core");
      const isNative = Capacitor.isNativePlatform();

      if (isNative) {
        try {
          const { waitForAdMob } = await import("../utils/adMobState");
          const status = await waitForAdMob();
          if (status.failed) {
            console.error("AdMob not ready — cannot show rewarded video");
            return;
          }
          const { AdMob, RewardAdPluginEvents } = await import("@capacitor-community/admob");
          const { getRewardedVideoId } = await import("../utils/adConfig");
          const adId = await getRewardedVideoId();
          await AdMob.prepareRewardVideoAd({ adId });

          const rewardPromise = new Promise((resolve) => {
            AdMob.addListener(RewardAdPluginEvents.Rewarded, () => resolve(true));
            AdMob.addListener(RewardAdPluginEvents.Dismissed, () => resolve(false));
          });

          await AdMob.showRewardVideoAd();
          const rewarded = await rewardPromise;

          if (rewarded && statsGatedPlayerId) {
            // Grant 5 additional lookups by decrementing the count by 5
            setStatsLookupCount(prev => Math.max(0, prev - 5));
            setLoadingDetail(true);
            try {
              const res = await axios.get(`${API}/players/${statsGatedPlayerId}/detail`);
              setSelectedPlayerDetail(res.data);
              setStatsGatedPlayerId(null);
            } catch (e) {
              console.error("Failed to load player detail:", e);
            } finally {
              setLoadingDetail(false);
            }
          }
        } catch (e) {
          console.error("AdMob rewarded error:", e);
        }
      } else {
        navigate("/subscribe");
      }
    } catch (e) {
      console.error("unlockStatsWithVideo error:", e);
    }
  };

  // Save team
  const saveTeam = async () => {
    if (!canSave) return;

    setSaving(true);
    setSuccess("");

    try {
      // Get fresh token for API call
      const token = await user.getIdToken(true);
      const authHeaders = { Authorization: `Bearer ${token}` };
      
      const teamData = {
        name: teamName.trim(),
        players: selectedPlayers.map(p => p.id),
        captain_id: captainId,
        vice_captain_id: viceCaptainId
      };

      if (existingTeam) {
        await axios.put(`${API}/teams/${existingTeam.id}`, teamData, { headers: authHeaders });
        setSuccess("Team updated successfully!");
      } else {
        const res = await axios.post(`${API}/teams`, teamData, { headers: authHeaders });
        setExistingTeam({ ...teamData, id: res.data.team_id });
        setSuccess("Team created successfully!");
      }
      hasUnsavedChangesRef.current = false;
      hapticSuccess();
      showInterstitial();
    } catch (e) {
      const errorMsg = e.response?.data?.detail || "Failed to save team";
      console.error("Failed to save team:", errorMsg);
      setSuccess("");
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };
  
  // Save as Draft Team (can be saved anytime)
  const saveDraftTeam = async () => {
    if (!canSave) return;
    
    setSavingDraft(true);
    setSuccess("");
    
    try {
      const token = await user.getIdToken(true);
      const authHeaders = { Authorization: `Bearer ${token}` };
      
      const draftData = {
        name: teamName.trim(),
        players: selectedPlayers.map(p => p.id),
        captain_id: captainId,
        vice_captain_id: viceCaptainId
      };
      
      await axios.post(`${API}/draft-teams`, draftData, { headers: authHeaders });
      setSuccess("Draft team saved! Apply it when the transfer window opens.");
      
      // Refresh draft team
      const draftRes = await axios.get(`${API}/draft-teams/my-draft`, { headers: authHeaders });
      if (draftRes.data && draftRes.data.draft_team) {
        setDraftTeam(draftRes.data);
      }
    } catch (e) {
      const errorMsg = e.response?.data?.detail || "Failed to save draft";
      console.error("Failed to save draft:", errorMsg);
      alert(errorMsg);
    } finally {
      setSavingDraft(false);
    }
  };
  
  // Apply Draft Team (only when transfer window is open)
  const applyDraftTeam = async () => {
    if (!draftTeam) return;
    
    setApplyingDraft(true);
    setSuccess("");
    
    try {
      const token = await user.getIdToken(true);
      const authHeaders = { Authorization: `Bearer ${token}` };
      
      await axios.post(`${API}/draft-teams/apply`, {}, { headers: authHeaders });
      setSuccess("Draft team applied successfully!");
      
      // Reload the page data
      window.location.reload();
    } catch (e) {
      const errorMsg = e.response?.data?.detail || "Failed to apply draft";
      console.error("Failed to apply draft:", errorMsg);
      alert(errorMsg);
    } finally {
      setApplyingDraft(false);
    }
  };
  
  // Load draft team into selection
  const loadDraftIntoSelection = () => {
    if (!draftTeam || !draftTeam.draft_team) return;
    
    const draft = draftTeam.draft_team;
    const draftPlayers = players.filter(p => draft.players.includes(p.id));
    
    setSelectedPlayers(draftPlayers);
    setCaptainId(draft.captain_id);
    setViceCaptainId(draft.vice_captain_id);
    setTeamName(draft.name);
    setDraftMode(false);
    setSuccess("Draft loaded! Make changes or save as your team.");
  };

  // Change Captain (Pro Max only, outside transfer window)
  const handleCaptainChange = async () => {
    if (!newCaptainId || !existingTeam) return;
    
    setChangingCaptain(true);
    try {
      const token = await user.getIdToken(true);
      const authHeaders = { Authorization: `Bearer ${token}` };
      
      await axios.put(`${API}/teams/captain-change`, {
        captain_id: newCaptainId
      }, { headers: authHeaders });
      
      // Update local state
      setCaptainId(newCaptainId);
      setExistingTeam(prev => ({ ...prev, captain_id: newCaptainId }));
      setShowCaptainChangeModal(false);
      setNewCaptainId(null);
      setSuccess("Captain changed successfully!");
      hapticSuccess();
    } catch (e) {
      const errorMsg = e.response?.data?.detail || "Failed to change captain";
      setError(errorMsg);
    } finally {
      setChangingCaptain(false);
    }
  };

  // Check if user can change captain (Pro Max, transfer window closed, has team, not used this week)
  const canChangeCaptain = () => {
    const userTier = getEffectiveTier(dbUser);
    if (userTier !== "pro_max" || transferWindow.is_open || !existingTeam) {
      return false;
    }
    const lastChange = dbUser?.last_captain_change;
    if (lastChange) {
      const lastChangeDate = new Date(lastChange);
      const now = new Date();
      // Dubai Saturday week boundary (UTC+4)
      const dubaiNow = new Date(now.getTime() + 4 * 60 * 60 * 1000);
      const dayOfWeek = dubaiNow.getUTCDay();
      const daysSinceSaturday = (dayOfWeek + 1) % 7;
      const weekStart = new Date(dubaiNow);
      weekStart.setUTCDate(dubaiNow.getUTCDate() - daysSinceSaturday);
      weekStart.setUTCHours(0, 0, 0, 0);
      const weekStartUtc = new Date(weekStart.getTime() - 4 * 60 * 60 * 1000);
      if (lastChangeDate >= weekStartUtc) {
        return false;
      }
    }
    return true;
  };

  // Change Vice Captain (Pro Plus and Pro Max, outside transfer window)
  const handleVcChange = async () => {
    if (!newVcId || !existingTeam) return;
    setChangingVc(true);
    try {
      const token = await user.getIdToken(true);
      const authHeaders = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/teams/vice-captain-change`, {
        vice_captain_id: newVcId
      }, { headers: authHeaders });
      setViceCaptainId(newVcId);
      setExistingTeam(prev => ({ ...prev, vice_captain_id: newVcId }));
      setShowVcChangeModal(false);
      setNewVcId(null);
      setSuccess("Vice captain changed successfully!");
      hapticSuccess();
    } catch (e) {
      const errorMsg = e.response?.data?.detail || "Failed to change vice captain";
      setError(errorMsg);
    } finally {
      setChangingVc(false);
    }
  };

  const canChangeViceCaptain = () => {
    const userTier = getEffectiveTier(dbUser);
    if (!["pro_plus", "pro_max"].includes(userTier) || transferWindow.is_open || !existingTeam) {
      return false;
    }
    const lastChange = dbUser?.last_vc_change;
    if (lastChange) {
      const lastChangeDate = new Date(lastChange);
      const now = new Date();
      // Dubai Saturday week boundary (UTC+4)
      const dubaiNow = new Date(now.getTime() + 4 * 60 * 60 * 1000);
      const dayOfWeek = dubaiNow.getUTCDay();
      const daysSinceSaturday = (dayOfWeek + 1) % 7;
      const weekStart = new Date(dubaiNow);
      weekStart.setUTCDate(dubaiNow.getUTCDate() - daysSinceSaturday);
      weekStart.setUTCHours(0, 0, 0, 0);
      const weekStartUtc = new Date(weekStart.getTime() - 4 * 60 * 60 * 1000);
      if (lastChangeDate >= weekStartUtc) {
        return false;
      }
    }
    return true;
  };

  if (!user) {
    return (
      <div className="text-center py-12" data-testid="team-builder-login-prompt">
        <Shield className="mx-auto text-slate-600 mb-4" size={48} />
        <p className="text-slate-400 mb-4">Please login to create your team</p>
        <Link to="/auth" className="text-emerald-400 hover:underline">Login</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-emerald-400" size={48} />
      </div>
    );
  }

  return (
    <div>
    <div className="space-y-4 pb-36" data-testid="team-builder-page">
      {/* RULES BANNER - Compact single line */}
      <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 flex items-center justify-between">
        <p className="text-slate-300 text-sm">
          <span className="text-white font-medium">11 players</span> · <span className="text-white font-medium">{creditBudget} Cr</span> budget · Max <span className="text-white font-medium">{isFinalsMode ? "2 teams" : "4 per team"}</span>
          {budgetBreakdown?.tier_bonus > 0 && <span className="text-amber-400 text-xs ml-1">(+{budgetBreakdown.tier_bonus} Cr bonus)</span>}
        </p>
        <button 
          onClick={() => setShowRules(true)}
          className="text-emerald-400 text-xs underline whitespace-nowrap ml-2"
        >
          Full Rules
        </button>
      </div>

      {/* Transfer Window Status */}
      {!transferWindow.is_open && existingTeam && (
        <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-xl p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="text-yellow-400 font-medium">Transfer window is closed</p>
              <p className="text-yellow-400/80 text-sm mt-1">
                You can view your current team below.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Vice Captain Change Button - Pro Plus and Pro Max */}
              {["pro_plus", "pro_max"].includes(getEffectiveTier(dbUser)) && (
                <button
                  onClick={() => setShowVcChangeModal(true)}
                  disabled={!canChangeViceCaptain()}
                  className={`px-3 py-2 rounded-lg font-medium transition-all flex items-center gap-1.5 text-sm ${
                    canChangeViceCaptain()
                      ? "bg-cyan-500 text-white hover:bg-cyan-600"
                      : "bg-cyan-500/30 text-cyan-300 cursor-not-allowed"
                  }`}
                  data-testid="change-vc-btn"
                  title={!canChangeViceCaptain() ? "Already used this week" : "Change vice captain (once per week)"}
                >
                  <RefreshCw size={14} /> Change VC
                </button>
              )}
              {/* Captain Change Button - Pro Max Only */}
              {getEffectiveTier(dbUser) === "pro_max" && (
                <button
                  onClick={() => setShowCaptainChangeModal(true)}
                  disabled={!canChangeCaptain()}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                    canChangeCaptain() 
                      ? "bg-purple-500 text-white hover:bg-purple-600" 
                      : "bg-purple-500/30 text-purple-300 cursor-not-allowed"
                  }`}
                  data-testid="change-captain-btn"
                  title={!canChangeCaptain() ? "Already used this week" : "Change captain (once per week)"}
                >
                  <RefreshCw size={16} /> Change Captain
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {!transferWindow.is_open && !existingTeam && (
        <div className="bg-emerald-500/20 border border-emerald-500/50 rounded-xl p-4">
          <p className="text-emerald-400 font-medium">Welcome! Create Your First Team</p>
          <p className="text-emerald-400/80 text-sm mt-1">
            New users can create their team anytime. Note: You'll only earn points from matches played after your team is created, and late-joiner penalties may apply.
          </p>
        </div>
      )}
      {transferWindow.is_open && transferWindow.window?.new_users_only && existingTeam && (
        <div className="bg-amber-500/20 border border-amber-500/50 rounded-xl p-4">
          <p className="text-amber-400 font-medium">Mid-Week Transfer - New Users Only</p>
          <p className="text-amber-400/80 text-sm mt-1">
            This window is only for new users who haven't created a team yet. Your team is locked.
          </p>
        </div>
      )}
      {transferWindow.is_open && transferWindow.window?.new_users_only && !existingTeam && (
        <div className="bg-emerald-500/20 border border-emerald-500/50 rounded-xl p-4">
          <p className="text-emerald-400 font-medium">Welcome! Mid-Week Transfer Window Open</p>
          <p className="text-emerald-400/80 text-sm mt-1">
            Create your team now. Note: You will only earn points from matches played after your team is created.
          </p>
        </div>
      )}

      {/* LIVE VALIDATION PANEL - Crystal Clear */}
      <div className="bg-slate-800/80 border border-slate-700 rounded-xl overflow-hidden">
        {/* Header */}
        <div className="bg-slate-900/50 px-4 py-3 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-semibold">Team Status</h3>
            <div className="flex items-center gap-3">
              <span className={`text-lg font-bold ${selectedPlayers.length === 11 ? 'text-emerald-400' : 'text-amber-400'}`}>
                {selectedPlayers.length}/11 Players
              </span>
              <span className={`text-lg font-bold ${displayCredits <= creditBudget ? 'text-emerald-400' : 'text-red-400'}`}>
                {displayCredits.toFixed(1)}/{creditBudget} Cr
              </span>
              {isViewOnly && totalCredits > originalCredits && (
                <span className="text-[10px] text-cyan-400 block">Current value: {totalCredits.toFixed(1)} Cr</span>
              )}
            </div>
          </div>
        </div>
        
        {/* Role Requirements Grid */}
        <div className="p-4">
          <p className="text-slate-400 text-xs mb-2 font-medium">ROLE REQUIREMENTS (min-max)</p>
          <div className="grid grid-cols-4 gap-2">
            {Object.entries(roleReqs).map(([role, req]) => {
              const count = roleCounts[role] || 0;
              const isValid = count >= req.min && count <= req.max;
              const needsMore = count < req.min;
              const isFull = count >= req.max;
              
              return (
                <div 
                  key={role} 
                  className={`p-3 rounded-lg border text-center transition-all ${
                    isValid 
                      ? 'bg-emerald-500/20 border-emerald-500/50' 
                      : needsMore
                        ? 'bg-red-500/20 border-red-500/50'
                        : 'bg-slate-700/50 border-slate-600'
                  }`}
                >
                  <p className="text-2xl">{req.icon}</p>
                  <p className={`font-bold text-xl ${isValid ? 'text-emerald-400' : needsMore ? 'text-red-400' : 'text-white'}`}>
                    {count}
                  </p>
                  <p className="text-xs text-slate-400">{req.label}</p>
                  <p className="text-[10px] text-slate-500">({req.min}-{req.max})</p>
                  {needsMore && (
                    <p className="text-[10px] text-red-400 mt-1">Need {req.min - count} more</p>
                  )}
                  {isFull && (
                    <p className="text-[10px] text-amber-400 mt-1">MAX</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Validation Issues - IN YOUR FACE */}
        {validationIssues.length > 0 && (
          <div className="px-4 pb-4">
            <p className="text-slate-400 text-xs mb-2 font-medium">TO COMPLETE YOUR TEAM</p>
            <div className="space-y-2">
              {validationIssues.map((issue, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center gap-2 p-2 rounded-lg ${
                    issue.type === "error" 
                      ? "bg-red-500/20 text-red-400" 
                      : "bg-amber-500/20 text-amber-400"
                  }`}
                >
                  {issue.type === "error" ? <AlertCircle size={16} /> : <Info size={16} />}
                  <span className="text-sm">{issue.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* All Good Message */}
        {canSave && (
          <div className="px-4 pb-4">
            <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/20 text-emerald-400">
              <CheckCircle size={18} />
              <span className="font-medium">Your team is ready to save!</span>
            </div>
          </div>
        )}
      </div>

      {/* Team Name Input */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
        <label className="block text-sm text-slate-400 mb-2">
          Team Name <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={teamName}
          onChange={(e) => setTeamName(e.target.value)}
          disabled={isViewOnly}
          placeholder="Enter your team name (e.g., Dream XI)"
          className={`w-full px-4 py-3 bg-slate-900 border rounded-lg text-white placeholder-slate-400 focus:outline-none transition-all ${
            !teamName.trim() ? 'border-amber-500/50' : 'border-slate-700 focus:border-emerald-500'
          } disabled:opacity-50`}
          data-testid="team-name-input"
        />
      </div>

      {/* Selected Players - Your Squad */}
      {selectedPlayers.length > 0 && (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-semibold">Your Squad ({selectedPlayers.length}/11)</h3>
            <div className="flex items-center gap-2 text-xs">
              <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded">C = 2x pts</span>
              <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded">VC = 1.5x pts</span>
            </div>
          </div>
          
          {/* Captain/VC Selection Prompt */}
          {(!captainId || !viceCaptainId) && (
            <div className="mb-3 p-2 bg-purple-500/20 border border-purple-500/30 rounded-lg">
              <p className="text-purple-400 text-sm">
                👆 Tap <Crown className="inline" size={14} /> for Captain or <Star className="inline" size={14} /> for Vice-Captain
              </p>
            </div>
          )}
          
          {/* Season / This Week toggle */}
          {existingTeam && (
            <div className="flex gap-2 mb-3" data-testid="points-view-toggle">
              <button
                onClick={() => setPointsView("season")}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  pointsView === "season"
                    ? "bg-emerald-500 text-white"
                    : "bg-slate-700 text-slate-400 hover:text-white"
                }`}
                data-testid="points-view-season"
              >
                Season
              </button>
              <button
                onClick={() => { setPointsView("week"); fetchWeeklyBreakdown(); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  pointsView === "week"
                    ? "bg-emerald-500 text-white"
                    : "bg-slate-700 text-slate-400 hover:text-white"
                }`}
                data-testid="points-view-week"
              >
                This week
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {selectedPlayers.map((player) => (
              <div 
                key={player.id}
                className={`flex items-center justify-between rounded-lg p-3 transition-all ${
                  captainId === player.id 
                    ? 'bg-yellow-500/20 border border-yellow-500/50' 
                    : viceCaptainId === player.id
                      ? 'bg-cyan-500/20 border border-cyan-500/50'
                      : 'bg-slate-900/50'
                }`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getTeamColor(player.country).bg} flex items-center justify-center text-white font-bold text-xs flex-shrink-0`}>
                    {player.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm font-medium flex items-center gap-2">
                      <span className="truncate">{player.name}</span>
                      {player.caps && player.caps.length > 0 && (
                        <span className="flex gap-0.5 flex-shrink-0">
                          {player.caps.filter(c => c.type === "orange").map((c, i) => (
                            <span key={`o${i}`} className="text-[10px]" title={`Orange Cap Week ${c.week}`}>🟠</span>
                          ))}
                          {player.caps.filter(c => c.type === "purple").map((c, i) => (
                            <span key={`p${i}`} className="text-[10px]" title={`Purple Cap Week ${c.week}`}>🟣</span>
                          ))}
                        </span>
                      )}
                      {captainId === player.id && <span className="px-1.5 py-0.5 bg-yellow-500 text-black text-xs font-bold rounded flex-shrink-0">C</span>}
                      {viceCaptainId === player.id && <span className="px-1.5 py-0.5 bg-cyan-500 text-black text-xs font-bold rounded flex-shrink-0">VC</span>}
                    </p>
                    <p className="text-slate-400 text-xs">{player.country} • {player.role} • {player.credits || player.base_credits}Cr</p>
                  </div>
                  {/* Player Points Column */}
                  {(player.total_points > 0 || pointsView === "week") && (
                    <div className="text-right flex-shrink-0 mr-2">
                      {pointsView === "season" ? (
                        <>
                          <p className="text-emerald-400 font-bold text-sm">{player.total_points}</p>
                          <p className="text-[10px] text-slate-500">season</p>
                        </>
                      ) : (
                        <>
                          <p className="text-emerald-400 font-bold text-sm">
                            {weeklyBreakdown?.player_breakdown?.[player.id]?.weekly_points ?? "—"}
                          </p>
                          {weeklyBreakdown?.player_breakdown?.[player.id]?.multiplier > 1 && (
                            <p className="text-[10px] text-amber-400">
                              {weeklyBreakdown.player_breakdown[player.id].is_captain ? "C×2" : "VC×1.5"}
                            </p>
                          )}
                          <p className="text-[10px] text-slate-500">this wk</p>
                        </>
                      )}
                    </div>
                  )}
                </div>
                {!isViewOnly && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); setCaptainId(player.id); if(viceCaptainId === player.id) setViceCaptainId(null); }}
                      className={`p-2 rounded-lg transition-all ${captainId === player.id ? 'bg-yellow-500 text-black' : 'bg-slate-700 text-slate-400 hover:bg-yellow-500/30 hover:text-yellow-400'}`}
                      title="Set as Captain (2x points)"
                    >
                      <Crown size={16} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); if(captainId !== player.id) setViceCaptainId(player.id); }}
                      disabled={captainId === player.id}
                      className={`p-2 rounded-lg transition-all ${viceCaptainId === player.id ? 'bg-cyan-500 text-black' : 'bg-slate-700 text-slate-400 hover:bg-cyan-500/30 hover:text-cyan-400'} disabled:opacity-30 disabled:cursor-not-allowed`}
                      title="Set as Vice-Captain (1.5x points)"
                    >
                      <Star size={16} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); togglePlayer(player); }}
                      className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30"
                      title="Remove player"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search & Filters */}
      {!isViewOnly && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search players..."
                className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500"
                data-testid="player-search-input"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-3 rounded-lg border transition-all flex items-center gap-2 ${
                showFilters ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' : 'bg-slate-800 border-slate-700 text-slate-400'
              }`}
            >
              <Filter size={18} />
              <ChevronDown className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} size={16} />
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-2 gap-3 bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Filter by Role</label>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                >
                  <option value="">All Roles</option>
                  <option value="Wicketkeeper">🧤 Wicketkeeper</option>
                  <option value="Batsman">🏏 Batsman</option>
                  <option value="All-Rounder">⭐ All-Rounder</option>
                  <option value="Bowler">🎯 Bowler</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Filter by Team</label>
                <select
                  value={countryFilter}
                  onChange={(e) => setCountryFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                >
                  <option value="">All Countries</option>
                  {countries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Player List */}
      {!isViewOnly && (
        <div className="space-y-2">
          <p className="text-sm text-slate-400">{filteredPlayers.length} players available</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredPlayers.map((player) => {
              const status = getPlayerStatus(player);
              
              return (
                <div 
                  key={player.id}
                  onClick={() => !isViewOnly && togglePlayer(player)}
                  className={`p-4 rounded-xl border cursor-pointer transition-all relative ${
                    status.selected 
                      ? 'bg-emerald-500/20 border-emerald-500' 
                      : status.canAdd 
                        ? 'bg-slate-800/50 border-slate-700/50 hover:border-emerald-500/50 hover:bg-slate-700/50' 
                        : 'bg-slate-800/30 border-slate-700/30 opacity-60'
                  }`}
                  data-testid={`player-card-${player.id}`}
                >
                  {/* Unavailable Reason Badge */}
                  {!status.canAdd && !status.selected && (
                    <div className="absolute top-2 right-2 px-2 py-1 bg-red-500/80 text-white text-[10px] rounded-full">
                      {status.reason}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getTeamColor(player.country).bg} flex items-center justify-center text-white font-bold text-xs`}>
                      {player.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <h3 className="font-semibold text-white truncate">{player.name}</h3>
                        {player.is_benched && (
                          <span className="text-[9px] px-1.5 py-0.5 bg-amber-500/20 text-amber-400 border border-amber-500/40 rounded-full flex-shrink-0" title={`Missed ${player.country_matches - player.matches_played} match(es) while ${player.country} played`}>
                            BENCHED
                          </span>
                        )}
                        {player.caps && player.caps.length > 0 && (
                          <span className="flex gap-0.5 flex-shrink-0">
                            {player.caps.filter(c => c.type === "orange").map((c, i) => (
                              <span key={`o${i}`} className="text-[10px]" title={`Orange Cap Week ${c.week}`}>🟠</span>
                            ))}
                            {player.caps.filter(c => c.type === "purple").map((c, i) => (
                              <span key={`p${i}`} className="text-[10px]" title={`Purple Cap Week ${c.week}`}>🟣</span>
                            ))}
                          </span>
                        )}
                      </div>
                      <p className="text-slate-400 text-sm">{player.country}</p>
                    </div>
                    {status.selected && (
                      <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                        <Check size={14} className="text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full border ${ROLE_COLORS[player.role]}`}>
                      {ROLE_REQUIREMENTS[player.role]?.icon} {player.role}
                    </span>
                    <span className="text-emerald-400 font-mono font-semibold">
                      {player.credits || player.base_credits || 8} Cr
                    </span>
                  </div>
                  
                  {/* Mini Stats Row */}
                  {player.matches_played > 0 && (
                    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-700/50">
                      <div className="flex items-center gap-1 text-[10px] text-slate-400">
                        <span className="text-slate-500">M:</span>
                        <span className="text-white">{player.matches_played}</span>
                      </div>
                      {player.total_runs > 0 && (
                        <div className="flex items-center gap-1 text-[10px] text-slate-400">
                          <span className="text-slate-500">R:</span>
                          <span className="text-white">{player.total_runs}</span>
                        </div>
                      )}
                      {player.total_wickets > 0 && (
                        <div className="flex items-center gap-1 text-[10px] text-slate-400">
                          <span className="text-slate-500">W:</span>
                          <span className="text-white">{player.total_wickets}</span>
                        </div>
                      )}
                      {player.total_catches > 0 && (
                        <div className="flex items-center gap-1 text-[10px] text-slate-400">
                          <span className="text-slate-500">Ct:</span>
                          <span className="text-white">{player.total_catches}</span>
                        </div>
                      )}
                      <div className="ml-auto">
                        <span className="text-[10px] text-cyan-400 font-medium">{player.total_points || 0} pts</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Tap for detail */}
                  <button
                    onClick={(e) => { e.stopPropagation(); openPlayerDetail(player.id); }}
                    className="w-full mt-2 text-[10px] text-slate-500 hover:text-cyan-400 transition-colors flex items-center justify-center gap-1"
                    data-testid={`player-detail-btn-${player.id}`}
                  >
                    <Info size={10} /> View Stats & Credit Breakdown
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}


      {/* Fixed Action Bar */}
      <div className="fixed left-0 right-0 px-4 z-40" style={{ bottom: "calc(5rem + env(safe-area-inset-bottom, 0px))" }}>
        <div className="max-w-7xl mx-auto">
          {/* Mini Status Bar */}
          <div className="bg-slate-900/95 backdrop-blur rounded-t-xl border border-b-0 border-slate-700 px-4 py-2 flex items-center justify-between text-sm">
            <div className="flex items-center gap-4">
              <span className={selectedPlayers.length === 11 ? 'text-emerald-400' : 'text-amber-400'}>
                {selectedPlayers.length}/11 players
              </span>
              <span className={displayCredits <= creditBudget ? 'text-emerald-400' : 'text-red-400'}>
                {totalCredits.toFixed(1)}/{creditBudget} Cr
              </span>
            </div>
            <div className="flex items-center gap-2">
              {captainId ? <span className="text-yellow-400">C ✓</span> : <span className="text-slate-500">C ✗</span>}
              {viceCaptainId ? <span className="text-cyan-400">VC ✓</span> : <span className="text-slate-500">VC ✗</span>}
            </div>
          </div>
          
          {/* Save Team Button */}
          <button
            onClick={saveTeam}
            disabled={saving || !canSave || !canSaveTeam || isViewOnly}
            className={`w-full font-semibold py-3 sm:py-4 rounded-b-xl transition-all flex items-center justify-center gap-2 shadow-lg ${
              canSave && canSaveTeam && !isViewOnly
                ? 'bg-emerald-500 text-white hover:bg-emerald-600' 
                : 'bg-slate-700 text-slate-400 cursor-not-allowed'
            }`}
            data-testid="save-team-btn"
          >
            {saving ? (
              <><Loader2 className="animate-spin" size={20} /> Saving...</>
            ) : !canSave ? (
              <><AlertCircle size={20} /> <span className="hidden sm:inline">Complete Requirements</span><span className="sm:hidden">Incomplete</span></>
            ) : !canSaveTeam ? (
              <><AlertCircle size={20} /> <span className="hidden sm:inline">Window Closed</span><span className="sm:hidden">Closed</span></>
            ) : isViewOnly ? (
              <><AlertCircle size={20} /> <span className="hidden sm:inline">View Only</span><span className="sm:hidden">View</span></>
            ) : (
              <><Shield size={20} /> {existingTeam ? 'Update' : 'Save'} Team</>
            )}
          </button>
        </div>
      </div>

      {/* Success Toast */}
      {success && (
        <div className="fixed top-4 left-4 right-4 z-50 animate-in slide-in-from-top">
          <div className="max-w-md mx-auto bg-emerald-500 text-white rounded-xl p-4 shadow-lg flex items-center gap-3">
            <CheckCircle size={24} />
            <span className="font-medium">{success}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="fixed top-4 left-4 right-4 z-50 animate-in slide-in-from-top">
          <div className="max-w-md mx-auto bg-red-500 text-white rounded-xl p-4 shadow-lg flex items-center gap-3">
            <AlertCircle size={24} />
            <span className="font-medium">{error}</span>
            <button onClick={() => setError("")} className="ml-auto text-white/80 hover:text-white">
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Full Rules Modal */}
      {showRules && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Team Selection Rules</h2>
              <button onClick={() => setShowRules(false)} className="text-slate-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4 text-slate-300">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                <h3 className="text-emerald-400 font-semibold mb-2">📋 Basic Rules</h3>
                <ul className="space-y-2 text-sm">
                  <li>• Select exactly <strong className="text-white">11 players</strong></li>
                  <li>• Stay within <strong className="text-white">{creditBudget} Crore</strong> budget</li>
                  <li>• {isFinalsMode ? "Pick from only 2 teams (no per-team limit)" : <span>Maximum <strong className="text-white">{maxPerCountry} players</strong> from same team</span>}</li>
                </ul>
              </div>
              
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <h3 className="text-blue-400 font-semibold mb-2">🧤 Role Requirements</h3>
                <ul className="space-y-2 text-sm">
                  <li>• <strong className="text-amber-400">Wicketkeepers:</strong> {roleReqs["Wicketkeeper"].min} to {roleReqs["Wicketkeeper"].max} players</li>
                  <li>• <strong className="text-blue-400">Batsmen:</strong> {roleReqs["Batsman"].min} to {roleReqs["Batsman"].max} players</li>
                  <li>• <strong className="text-purple-400">All-Rounders:</strong> {roleReqs["All-Rounder"].min} to {roleReqs["All-Rounder"].max} players</li>
                  <li>• <strong className="text-red-400">Bowlers:</strong> {roleReqs["Bowler"].min} to {roleReqs["Bowler"].max} players</li>
                </ul>
              </div>
              
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <h3 className="text-yellow-400 font-semibold mb-2">👑 Captain & Vice-Captain</h3>
                <ul className="space-y-2 text-sm">
                  <li>• <strong className="text-yellow-400">Captain (C):</strong> Earns 2x points</li>
                  <li>• <strong className="text-cyan-400">Vice-Captain (VC):</strong> Earns 1.5x points</li>
                  <li>• Choose wisely - pick your best performers!</li>
                </ul>
              </div>
              
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                <h3 className="text-purple-400 font-semibold mb-2">💡 Tips</h3>
                <ul className="space-y-2 text-sm">
                  <li>• Balance your team across all roles</li>
                  <li>• Don't spend all Crore on one player</li>
                  <li>• Mix expensive stars with budget picks</li>
                  <li>• Consider match conditions and form</li>
                </ul>
              </div>
            </div>
            
            <button
              onClick={() => setShowRules(false)}
              className="w-full mt-4 bg-emerald-500 text-white font-semibold py-3 rounded-lg hover:bg-emerald-600 transition-all"
            >
              Got it!
            </button>
          </div>
        </div>
      )}
      {/* Player Detail Overlay */}
      {(selectedPlayerDetail || loadingDetail) && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center"
          onClick={() => { setSelectedPlayerDetail(null); }}
          data-testid="player-detail-overlay"
        >
          <div 
            className="bg-slate-800 border border-slate-700 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {loadingDetail && !selectedPlayerDetail ? (
              <div className="p-8 flex items-center justify-center">
                <Loader2 className="animate-spin text-emerald-400" size={32} />
              </div>
            ) : selectedPlayerDetail && (
              <>
                {/* Header */}
                <div className="sticky top-0 bg-slate-800 border-b border-slate-700 p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${getTeamColor(selectedPlayerDetail.player.country).bg} flex items-center justify-center text-white font-bold text-sm`}>
                      {selectedPlayerDetail.player.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg">{selectedPlayerDetail.player.name}</h3>
                      <p className="text-slate-400 text-sm">{selectedPlayerDetail.player.country} • {selectedPlayerDetail.player.role}</p>
                    </div>
                  </div>
                  <button onClick={() => setSelectedPlayerDetail(null)} className="text-slate-400 hover:text-white p-1">
                    <X size={22} />
                  </button>
                </div>

                {/* Player Value Section */}
                <div className="p-4 border-b border-slate-700/50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-slate-400 text-sm font-medium">Player Value</span>
                    <span className="text-emerald-400 font-bold text-2xl">{selectedPlayerDetail.credits} Crore</span>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Base</span>
                      <span className="text-white">{selectedPlayerDetail.credit_breakdown.base}</span>
                    </div>
                    {selectedPlayerDetail.stats.matches_played > 0 && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Avg Points/Match</span>
                          <span className="text-cyan-400">{selectedPlayerDetail.credit_breakdown.avg_points_per_match}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Performance Bonus</span>
                          <span className="text-amber-400">+{selectedPlayerDetail.credit_breakdown.performance_bonus}</span>
                        </div>
                        {selectedPlayerDetail.credit_breakdown.cap_bonus > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">Cap Bonus</span>
                            <span className="text-purple-400">+{selectedPlayerDetail.credit_breakdown.cap_bonus}</span>
                          </div>
                        )}
                        <div className="border-t border-slate-700 pt-2 flex justify-between text-sm">
                          <span className="text-slate-400">Final Value</span>
                          <span className="text-emerald-400 font-bold">{selectedPlayerDetail.credit_breakdown.final_credits} Crore</span>
                        </div>
                      </>
                    )}
                    <p className="text-[10px] text-slate-500 mt-1">{selectedPlayerDetail.credit_breakdown.formula}</p>
                  </div>
                </div>

                {/* Caps Section */}
                {selectedPlayerDetail.caps && selectedPlayerDetail.caps.length > 0 && (
                  <div className="px-4 pb-2 pt-2 border-b border-slate-700/50">
                    <h4 className="text-slate-400 text-sm font-medium mb-2">Awards</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedPlayerDetail.caps.map((cap, i) => (
                        <span key={i} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${
                          cap.type === "orange" 
                            ? "bg-orange-500/20 text-orange-400 border border-orange-500/30" 
                            : "bg-purple-500/20 text-purple-400 border border-purple-500/30"
                        }`}>
                          {cap.type === "orange" ? "🟠" : "🟣"} 
                          {cap.type === "orange" ? "Orange" : "Purple"} Cap - Week {cap.week}
                          {cap.runs && ` (${cap.runs} runs)`}
                          {cap.wickets && ` (${cap.wickets} wkts)`}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Stats Grid */}
                <div className="p-4 border-b border-slate-700/50">
                  <h4 className="text-slate-400 text-sm font-medium mb-3">Tournament Stats</h4>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { label: "Matches", value: selectedPlayerDetail.stats.matches_played, color: "text-white" },
                      { label: "Points", value: selectedPlayerDetail.stats.total_points, color: "text-emerald-400" },
                      { label: "Avg Pts", value: selectedPlayerDetail.stats.avg_points, color: "text-cyan-400" },
                      { label: "Runs", value: selectedPlayerDetail.stats.total_runs, color: "text-white" },
                      { label: "4s", value: selectedPlayerDetail.stats.total_fours, color: "text-amber-400" },
                      { label: "6s", value: selectedPlayerDetail.stats.total_sixes, color: "text-amber-400" },
                      { label: "Wickets", value: selectedPlayerDetail.stats.total_wickets, color: "text-red-400" },
                      { label: "Economy", value: selectedPlayerDetail.stats.economy_rate || "-", color: "text-cyan-400" },
                      { label: "Catches", value: selectedPlayerDetail.stats.total_catches, color: "text-blue-400" },
                      { label: "Stumpings", value: selectedPlayerDetail.stats.total_stumpings, color: "text-purple-400" },
                      { label: "Run Outs", value: selectedPlayerDetail.stats.total_run_outs, color: "text-orange-400" },
                    ].map(stat => (
                      <div key={stat.label} className="bg-slate-900/50 rounded-lg p-2 text-center">
                        <p className={`font-bold text-lg ${stat.color}`}>{stat.value}</p>
                        <p className="text-[10px] text-slate-500">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Match-by-Match Performances */}
                {selectedPlayerDetail.performances.length > 0 && (
                  <div className="p-4">
                    <h4 className="text-slate-400 text-sm font-medium mb-3">Match Performances</h4>
                    <div className="space-y-2">
                      {selectedPlayerDetail.performances.map((perf, idx) => (
                        <div key={idx} className="bg-slate-900/50 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white text-sm font-medium">{perf.match_label || `Match ${perf.match_id}`}</span>
                            <span className="text-emerald-400 font-bold">{perf.points} pts</span>
                          </div>
                          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-400">
                            {perf.runs > 0 && <span>{perf.runs} runs</span>}
                            {perf.fours > 0 && <span>{perf.fours} fours</span>}
                            {perf.sixes > 0 && <span>{perf.sixes} sixes</span>}
                            {perf.wickets > 0 && <span>{perf.wickets} wkts</span>}
                            {perf.catches > 0 && <span>{perf.catches} ct</span>}
                            {perf.stumpings > 0 && <span>{perf.stumpings} st</span>}
                            {perf.run_outs > 0 && <span>{perf.run_outs} ro</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Captain Change Modal - Pro Max Only */}
      {showCaptainChangeModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-md max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-white font-bold text-lg flex items-center gap-2">
                <RefreshCw className="text-purple-400" size={20} />
                Change Captain
              </h3>
              <button
                onClick={() => { setShowCaptainChangeModal(false); setNewCaptainId(null); }}
                className="text-slate-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4">
              <p className="text-slate-400 text-sm mb-4">
                As a Pro Max user, you can change your captain once per week outside the transfer window.
                Select your new captain from your current team:
              </p>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {selectedPlayers.filter(p => p.id !== captainId).map(player => (
                  <button
                    key={player.id}
                    onClick={() => setNewCaptainId(player.id)}
                    className={`w-full p-3 rounded-lg border transition-all flex items-center justify-between ${
                      newCaptainId === player.id
                        ? "bg-purple-500/20 border-purple-500/50"
                        : "bg-slate-700/50 border-slate-600 hover:border-slate-500"
                    }`}
                    data-testid={`captain-option-${player.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-white font-bold text-xs">
                        {player.name?.[0]}
                      </div>
                      <div className="text-left">
                        <p className="text-white font-medium text-sm">{player.name}</p>
                        <p className="text-slate-400 text-xs">{player.country}</p>
                      </div>
                    </div>
                    {newCaptainId === player.id && (
                      <Check className="text-purple-400" size={20} />
                    )}
                  </button>
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => { setShowCaptainChangeModal(false); setNewCaptainId(null); }}
                  className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg font-medium hover:bg-slate-500 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCaptainChange}
                  disabled={!newCaptainId || changingCaptain}
                  className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="confirm-captain-change-btn"
                >
                  {changingCaptain ? <Loader2 className="animate-spin" size={16} /> : <Crown size={16} />}
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vice Captain Change Modal - Pro Plus and Pro Max */}
      {showVcChangeModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-md max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-white font-bold text-lg flex items-center gap-2">
                <RefreshCw className="text-cyan-400" size={20} />
                Change Vice Captain
              </h3>
              <button
                onClick={() => { setShowVcChangeModal(false); setNewVcId(null); }}
                className="text-slate-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4">
              <p className="text-slate-400 text-sm mb-4">
                Select your new vice captain from your current team.
                You can change your vice captain once per week.
              </p>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {selectedPlayers.filter(p => p.id !== viceCaptainId && p.id !== captainId).map(player => (
                  <button
                    key={player.id}
                    onClick={() => setNewVcId(player.id)}
                    className={`w-full p-3 rounded-lg border transition-all flex items-center justify-between ${
                      newVcId === player.id
                        ? "bg-cyan-500/20 border-cyan-500/50"
                        : "bg-slate-700/50 border-slate-600 hover:border-slate-500"
                    }`}
                    data-testid={`vc-option-${player.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white font-bold text-xs">
                        {player.name?.[0]}
                      </div>
                      <div className="text-left">
                        <p className="text-white font-medium text-sm">{player.name}</p>
                        <p className="text-slate-400 text-xs">{player.country}</p>
                      </div>
                    </div>
                    {newVcId === player.id && (
                      <Check className="text-cyan-400" size={20} />
                    )}
                  </button>
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => { setShowVcChangeModal(false); setNewVcId(null); }}
                  className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg font-medium hover:bg-slate-500 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleVcChange}
                  disabled={!newVcId || changingVc}
                  className="flex-1 px-4 py-2 bg-cyan-500 text-white rounded-lg font-medium hover:bg-cyan-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="confirm-vc-change-btn"
                >
                  {changingVc ? <Loader2 className="animate-spin" size={16} /> : <Crown size={16} />}
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Gated Modal */}
      {statsGatedPlayerId && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-sm p-6 text-center">
            <div className="w-16 h-16 mx-auto bg-amber-500/20 rounded-full flex items-center justify-center mb-4">
              <Shield className="text-amber-400" size={32} />
            </div>
            <h3 className="text-white font-bold text-lg mb-2">Stats Limit Reached</h3>
            <p className="text-slate-400 text-sm mb-4">
              You've used your {getStatsLookupLimit()} free player stat lookup{getStatsLookupLimit() > 1 ? 's' : ''} for this session.
              {isNativePlatform 
                ? " Watch a short video to unlock this player's stats."
                : " Download the APL app for unlimited player stats."}
            </p>
            <div className="space-y-2">
              {isNativePlatform ? (
                <button
                  onClick={unlockStatsWithVideo}
                  className="w-full px-4 py-3 bg-amber-500 text-black rounded-lg font-bold hover:bg-amber-400 transition-all flex items-center justify-center gap-2"
                  data-testid="watch-video-stats-btn"
                >
                  <Star size={18} />
                  Watch Video to Unlock
                </button>
              ) : (
                <>
                  <a
                    href="https://apps.apple.com/app/apl-fantasy-cricket"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full px-4 py-3 bg-slate-900 text-white rounded-lg font-bold hover:bg-slate-700 transition-all flex items-center justify-center gap-2 border border-slate-600"
                    data-testid="download-ios-btn"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                    Download on App Store
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.apnapremierleague.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-lg font-bold hover:opacity-90 transition-all flex items-center justify-center gap-2"
                    data-testid="download-android-btn"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                    Google Play
                  </a>
                </>
              )}
              <button
                onClick={() => setStatsGatedPlayerId(null)}
                className="w-full px-4 py-2 text-slate-400 hover:text-white transition-all"
              >
                Cancel
              </button>
            </div>
            <p className="text-slate-500 text-xs mt-4">
              {isNativePlatform 
                ? "Upgrade to Pro for more lookups or Pro Max for unlimited stats"
                : "Get the APL app for rewarded video unlocks and offline access"}
            </p>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
